import TenLoading from '../loading';
import TenTableTable from './table-table.js';
import Emitter from '../scripts/mixins/emitter';
import { getColumnsTree, traverseTree, getColumnsTreeByProps } from './util';
import {
  addResizeListener,
  removeResizeListener,
} from '../scripts/utils/resize-observer';
import { getValueByPath } from '../scripts/utils/helper';

const name = 'ten-table';

export default {
  name,
  provide() {
    return {
      table: this,
    };
  },
  mixins: [Emitter],
  props: {
    /**
     * 表格数据
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * column配置对象数组
     * 与使用<ten-table-column>效果相同
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * 行的唯一标识，必填,
     * 展开时候需要通过 rowKe y获取指定的折叠行
     * 字符串支持 data.rowKey
     */
    rowKey: {
      type: [String, Function],
      default: 'id',
    },
    /**
     * 自定义行配置，可以配置对象或者是返回配置的函数 (row, $index) => rowProps
     */
    rowProps: [Object, Function],
    /**
     * 最小宽度，小于时才触发折叠
     */
    minWidth: Number,
    /**
     * 表格高度。
     * 合法的值为数字或者单位为 px 的高度。
     */
    height: [Number, String],
    /**
     * Table 的最大高度。
     * 合法的值为数字或者单位为 px 的高度。
     */
    maxHeight: [Number, String],
    /**
     * 是否加载中
     */
    loading: Boolean,
    /**
     * 无数据提醒,
     * 支持同名slot
     */
    emptyText: {
      type: String,
      default: '暂无数据',
    },
    /**
     * 已选中的行rowKey数组,
     * 支持.sync
     */
    selectedRowKeys: {
      type: Array,
      default: () => [],
    },
    /**
     * 隐藏表头
     */
    hideHeader: Boolean,
    /**
     * 已展开的行rowKey数组,
     * 有列配置设置了type="expand"有效，
     * 支持.sync
     */
    expandedRowKeys: {
      type: Array,
      default: () => [],
    },
    /**
     * 是否可伸缩列
     */
    resizable: {
      type: Boolean,
      default: false,
    },
    /**
     * resizable 光标类名
     */
    resizableHandleClass: {
      type: String,
      default: 'resizable-handle',
    },
    /**
     * 拖拽排序（行）
     */
    dragSort: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tbodyScrollTop: 0,
      tableWrapperWidth: undefined,
      sortOrder: undefined,
      sortColumn: {},
      scrollStatus: undefined,
      hoverRowIndex: undefined,
      cells: {},
      selectionMap: {},
    };
  },
  computed: {
    className() {
      return ['ten-table', 'ten-table--vue'];
    },
    innderTableWidth() {
      return this.scrollableX ? this.minWidth : this.tableWrapperWidth;
    },
    scrollableX() {
      return this.tableWrapperWidth < this.minWidth;
    },
    sortedData() {
      if (
        typeof this.sortColumn.sorter === 'function'
        && this.sortOrder
        && this.sortColumn.sortable
      ) {
        return this.data
          .slice(0)
          .sort((a, b) => this.sortColumn.sorter(a, b, this.sortOrder));
      }
      return this.data;
    },
  },
  watch: {
    scrollStatus(newStatus, oldStatus) {
      this.$refs[`${oldStatus}Table`] && this.$refs[`${oldStatus}Table`].onResize();
      this.$refs[`${oldStatus}Table`] && this.$refs[`${oldStatus}Table`].$el.classList.remove(`is-scrolling--${oldStatus}`);
      this.$refs[`${newStatus}Table`] && this.$refs[`${newStatus}Table`].$el.classList.add(`is-scrolling--${newStatus}`);
    },
    minWidth() {
      this.resize();
    },
    height() {
      this.resize();
    },
    maxHeight() {
      this.resize();
    },
  },
  created() {
    this.$on('sort-change', this.onSortChange);
  },
  mounted() {
    if (this.$el) {
      addResizeListener(this.$el, this.resize);
    }
  },
  beforeDestroy() {
    if (this.$el) {
      removeResizeListener(this.$el, this.resize);
    }
  },
  render() {
    let columnsTree = [];
    let defaultSlots;
    const sortInfo = {
      order: this.sortOrder,
      column: this.sortColumn,
    };
    if (this.$scopedSlots.default) {
      defaultSlots = this.$scopedSlots.default();
      columnsTree = getColumnsTree(defaultSlots, { sortInfo });
    } else if (this.columns && this.columns.length > 0) {
      columnsTree = getColumnsTreeByProps(this.columns, { sortInfo });
    }

    if (this.resizable) {
      columnsTree = this._resizableColumns(columnsTree);
    }

    if (columnsTree.length > 0) {
      // 只允许第一层表头设置fixed，其子节点跟随父节点
      const fixedLeft = columnsTree.filter(v => v.fixed === 'left');
      const fixedRight = columnsTree.filter(v => v.fixed === 'right');

      columnsTree = fixedLeft.concat(
        columnsTree.filter(v => v.fixed === undefined),
        fixedRight
      );

      const columns = [];
      const columnDepth = traverseTree(columnsTree, 1, columns);
      return (
        <div class={this.className}>
          {defaultSlots ? <div>{defaultSlots}</div> : null}
          <TenLoading loading={this.loading}>
            <TenTableTable
              ref="baseTable"
              columnsTree={columnsTree}
              columns={columns}
              depth={columnDepth}
              data={this.sortedData}
              rowProps={this.rowPropsFun}
              emptyText={this.$scopedSlots.emptyText || this.emptyText}
              scrollTop={this.tbodyScrollTop}
              innderTableWidth={this.innderTableWidth}
              hoverRowIndex={this.hoverRowIndex}
              onScroll={this.onScroll}
              onResize={this.onResize}
              onHoverChange={this.onHoverChange}
            />
            {fixedLeft.length > 0 && this.scrollableX ? (
              <TenTableTable
                ref="leftTable"
                columnsTree={columnsTree}
                columns={columns}
                depth={columnDepth}
                data={this.sortedData}
                rowProps={this.rowPropsFun}
                emptyText={this.$scopedSlots.emptyText || this.emptyText}
                scrollTop={this.tbodyScrollTop}
                innderTableWidth={this.innderTableWidth}
                hoverRowIndex={this.hoverRowIndex}
                fixedColumns={fixedLeft}
                fixed="left"
                sizeData={this.sizeData}
                onScroll={this.onScroll}
                onHoverChange={this.onHoverChange}
              />
            ) : null}
            {fixedRight.length > 0 && this.scrollableX ? (
              <TenTableTable
                ref="rightTable"
                columnsTree={columnsTree}
                columns={columns}
                depth={columnDepth}
                data={this.sortedData}
                rowProps={this.rowPropsFun}
                emptyText={this.$scopedSlots.emptyText || this.emptyText}
                scrollTop={this.tbodyScrollTop}
                innderTableWidth={this.innderTableWidth}
                hoverRowIndex={this.hoverRowIndex}
                fixedColumns={fixedRight}
                fixed="right"
                sizeData={this.sizeData}
                onScroll={this.onScroll}
                onHoverChange={this.onHoverChange}
              />
            ) : null}
          </TenLoading>
        </div>
      );
    }
    return null;
  },
  methods: {
    getRowKey(row) {
      if (typeof this.rowKey === 'function') {
        return this.rowKey(row);
      } if (typeof this.rowKey === 'string') {
        const path = `${this.rowKey}`;
        return getValueByPath(row, path);
      }
    },
    changeSelected(selectedKeys) {
      const { data, getRowKey, selectionMap } = this;
      const selections = selectedKeys.map((key) => {
        if (typeof key === 'undefined') return undefined;

        // 缓存之前获取到的值 防止 data 更改后获取不到
        let value = data.find(row => getRowKey(row) === key) || undefined;
        if (value) {
          this.selectionMap[key] = value;
        } else {
          value = selectionMap[key];
        }

        return value;
      });

      /**
       * 选中变化事件
       * @param {Array} selectedKeys 选中的 keys
       * @param {Array} selections 选中值对应的数据，如果没有则是 undefined
       */
      this.$emit('selectedChange', selectedKeys, selections);
    },
    rowPropsFun(...args) {
      if (typeof this.rowProps === 'object') {
        return this.rowProps;
      } if (typeof this.rowProps === 'function') {
        return this.rowProps(...args);
      }
      return this.rowProps;
    },
    setRefTables(fn) {
      const refTables = ['baseTable', 'leftTable', 'rightTable'];
      refTables.forEach((refTable) => {
        if (this.$refs[refTable]) {
          fn(this.$refs[refTable]);
        }
      });
    },
    /**
     * @public
     *
     * Table 隐藏切换为显示时，需要重新计算尺寸。默认已经对 ten 组件触发的切换（如 modal, tabs ）与路由切换做了处理
     */
    resize() {
      this.broadcastAll('do-layout');
    },
    addCell(cell) {
      this.cells[cell.id] = cell;
    },
    removeCell(cell) {
      if (cell === this.cells[cell.id]) {
        delete this.cells[cell.id];
      }
    },
    updateInvisibleCell(pureId, visibleCell) {
      const ids = ['base', 'left', 'right'].map(item => `${item}-${pureId}`);
      ids.forEach((id) => {
        if (this.cells[id] && this.cells[id].invisible && visibleCell.$el) {
          this.cells[id].syncFromVisibleCell(visibleCell);
        }
      });
    },
    onScroll({ tbodyScrollTop, scrollStatus, target }) {
      if (scrollStatus && this.scrollStatus !== scrollStatus) {
        this.scrollStatus = scrollStatus;
      }
      this.setRefTables((refTable) => {
        if (refTable !== target) {
          refTable.setScrollTop(tbodyScrollTop);
        }
      });
    },
    onResize(tableWrapperEl) {
      if (tableWrapperEl) {
        this.tableWrapperWidth = tableWrapperEl.offsetWidth;
      }
    },
    onSortChange(order, column) {
      this.sortOrder = order;
      this.sortColumn = column;
      /**
       * 排序改变事件
       * @param {Object} column 触发排序的列
       * @param {String} order 新的排序方式
       */
      this.$emit('sort', column, order);
    },
    onHoverChange(i) {
      // this.hoverRowIndex = i;
      this.setRefTables((refTable) => {
        refTable.setBodyHover(i);
      });
    },
    // 增强 columns 的功能，让其适合控制单元格的宽度
    _resizableColumns(columns = []) {
      // 为了适配带 children 的 column 而另外计算下标
      let colIndex = 0;

      const enhanceColumns = columns => columns.map((_column) => {
        const column = _column;
        if (!column.children) {
          // 给表头的 th 节点添加 resizable 的 class 名
          const _className = column.className;
          if (_className === undefined) {
            column.className = 'resizable';
          } else if (typeof _className === 'string') {
            column.className += ' resizable';
          } else {
            column.className = [..._className, 'resizable'];
          }

          // 这是为了在不改变原有数据的情况下，增强 title
          const _title = column.title;
          // 保存当前的 col 下标
          const _i = colIndex;
          colIndex += 1;

          column.title = h => h('div', { class: 'resizable-content' }, [
            typeof _title === 'function' ? _title(h) : _title,
            this._resizableDom(column, _i),
          ]);
        } else {
          column.children = enhanceColumns(column.children);
        }
        return column;
      });

      return enhanceColumns(columns);
    },
    // 单元格边界的节点
    _resizableDom(column, i) {
      return (
        <span
          class={this.resizableHandleClass}
          onClick={e => e.stopPropagation()}
          onMousedown={e => this._handelResizableWidth(e, i, column)}
        ></span>
      );
    },
    // 控制宽度变化
    _handelResizableWidth(event, index, _column) {
      const column = _column;
      event.stopPropagation();
      // col 的节点
      const colgroups = [];
      this.$el.querySelectorAll('colgroup').forEach((colgroup) => {
        colgroups.push(colgroup.querySelectorAll('col'));
      });

      // 获取节点实际宽度
      const thDom = event.path.find(dom => dom.className.indexOf('resizable') > 0);
      column.width = thDom.offsetWidth;

      const initailWidth = column.width;
      const startX = event.pageX;

      // 监听位置的变化，并计算长度
      let stop = true;

      const mousemoveFn = (e) => {
        if (!stop) return false;
        // 控制宽度变化度的关键
        colgroups.forEach((_colgroup) => {
          const colgroup = _colgroup;
          column.width = `${initailWidth + e.pageX - startX}px`;
          colgroup[index].style.width = column.width;
        });
      };

      const mouseupFn = () => {
        stop = false;
        /**
         * 可伸缩列在拖拽结束后的事件
         * @param {string} width 拖拽后的列宽
         * @param {object} column 发生拖拽所在列
         */
        this.$emit('columnWidthChange', column.width, column);
        document.removeEventListener('mousemove', mousemoveFn);
        document.removeEventListener('mouseup', mouseupFn);
      };

      document.addEventListener('mousemove', mousemoveFn);
      document.addEventListener('mouseup', mouseupFn);
    },
  },
};
